import { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { dayjs } from "Config/dayjs";
import { RichEditor, RichEditorInstance } from "Pages/Project/Overview/Components/RichEditor/RichEditor";
import { extractIdFromUId } from "../utils/extractIdFromUId";
import { groupListStore } from "Pages/Project/Groups/Stores/GroupsStore";
import { projectsDataStore } from "Stores";

interface EmailTextEditorProps {
  initialContent?: string;
  initialData?: API_MailResponse;
}

export interface EmailTextEditorInstance {
  returnContent: () => string;
}

export const EmailTextEditor = observer(
  forwardRef<EmailTextEditorInstance, EmailTextEditorProps>(({ initialContent, initialData }, ref) => {
    const { t } = useTranslation(I18N_NS.PROJECT);
    const editor = useRef<RichEditorInstance>();

    const returnContent = () => {
      return editor.current.getHtml();
    };

    const getEditorInitialContent = () => {
      let content: string = "";
      const signature = projectsDataStore.getCurrentProjectEmailSignature();
      console.log("getEditorInitialContent signature", signature);
      if (signature.showSignature && signature.signature) {
        content = content + `<p></p>${signature.signature}`;
      }
      if (initialContent) {
        if (!signature.showSignature) {
          content = content + `<p></p>`;
        }
        if (initialData.type !== "DRAFT") {
          const head = getHeadForQuote();
          content = content + `<p>\n>>></p>${head}\r${initialContent}<p>\n<<<<</p>`;
        } else {
          content = initialContent;
        }
      }
      if (!content.length) {
        content = " ";
      }
      return content;
    };

    const getHeadForQuote = () => {
      if (!initialData) {
        return "";
      }
      const formatReceiver = (uid: string) => {
        const record = initialData.correspondents[uid];

        // ****** This should be a temp workaround, check comments in pave-4066 ******
        if (!record) {
          const { value, type } = extractIdFromUId(uid);
          if (type === "group") {
            return groupListStore.getGroupNamesByIds([+value])[0] || uid;
          }
        }
        // ****** --------------------------------------------------- *******

        return record ? `${record.name} (${record.email})` : uid;
      };
      const fromString = `${t("From")}: ${formatReceiver(initialData.from)}`;
      const sentString = `${t("Sent")}: ${dayjs(initialData.timestamp).format("LLL")}`;
      const toString = `${t("To")}: ${initialData.to?.map(formatReceiver).join(", ")}`;
      const subjectString = `${t("Subject")}: ${initialData.subject}`;
      let head = `<p><br>${fromString}<br>${sentString}<br>${toString}<br>${subjectString}<p>`;

      if (initialData.cc?.length) {
        const ccString = `${t("CC")}: ${initialData.cc.map(formatReceiver).join(", ")}`;
        head += `<br>${ccString}`;
      }
      return head;
    };

    const preservedInitialContent = useMemo(getEditorInitialContent, [initialContent]);

    useImperativeHandle(
      ref,
      () => {
        return {
          returnContent: returnContent,
        };
      },
      []
    );

    return <RichEditor withFontFamily={false} initialValue={preservedInitialContent} ref={editor} />;
  })
);
